import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import QuotationList from './components/QuotationList';
import QuotationForm from './components/QuotationForm';
import QuotationPreview from './components/QuotationPreview';
import QuotationStats from './components/QuotationStats';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';



const QuotationManagement = () => {
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'preview'
  const [isEditing, setIsEditing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Mock quotations data
  useEffect(() => {
    const mockQuotations = [
      {
        id: 1,
        number: 'PRE-2024-001',
        clientId: 1,
        clientName: 'Tecnología Avanzada S.L.',
        date: '2024-01-15',
        expiryDate: '2024-02-15',
        status: 'approved',
        items: [
          {
            id: 1,
            productId: 1,
            productName: 'Laptop Dell XPS 13',
            productCode: 'LAP-001',
            quantity: 2,
            unitPrice: 1299.99,
            total: 2599.98
          },
          {
            id: 2,
            productId: 2,
            productName: 'Monitor Samsung 27" 4K',
            productCode: 'MON-002',
            quantity: 2,
            unitPrice: 399.99,
            total: 799.98
          }
        ],
        notes: 'Entrega en oficina central. Instalación incluida.',
        discount: 5,
        taxRate: 21,
        subtotal: 3399.96,
        discountAmount: 169.998,
        taxableAmount: 3229.962,
        taxAmount: 678.292,
        total: 3908.254
      },
      {
        id: 2,
        number: 'PRE-2024-002',
        clientId: 2,
        clientName: 'Innovación Digital S.A.',
        date: '2024-01-18',
        expiryDate: '2024-02-18',
        status: 'sent',
        items: [
          {
            id: 3,
            productId: 4,
            productName: 'Impresora HP LaserJet Pro',
            productCode: 'IMP-004',
            quantity: 1,
            unitPrice: 249.99,
            total: 249.99
          }
        ],
        notes: '',
        discount: 0,
        taxRate: 21,
        subtotal: 249.99,
        discountAmount: 0,
        taxableAmount: 249.99,
        taxAmount: 52.498,
        total: 302.488
      },
      {
        id: 3,
        number: 'PRE-2024-003',
        clientId: 3,
        clientName: 'Soluciones Empresariales Ltd.',
        date: '2024-01-20',
        expiryDate: '2024-02-20',
        status: 'draft',
        items: [
          {
            id: 4,
            productId: 6,
            productName: 'Silla Ergonómica Oficina',
            productCode: 'SIL-006',
            quantity: 10,
            unitPrice: 199.99,
            total: 1999.9
          }
        ],
        notes: 'Presupuesto para renovación de oficina.',
        discount: 10,
        taxRate: 21,
        subtotal: 1999.9,
        discountAmount: 199.99,
        taxableAmount: 1799.91,
        taxAmount: 377.981,
        total: 2177.891
      },
      {
        id: 4,
        number: 'PRE-2024-004',
        clientId: 4,
        clientName: 'Consultoría Integral S.L.',
        date: '2024-01-10',
        expiryDate: '2024-01-25',
        status: 'expired',
        items: [
          {
            id: 5,
            productId: 3,
            productName: 'Teclado Mecánico Logitech',
            productCode: 'TEC-003',
            quantity: 5,
            unitPrice: 129.99,
            total: 649.95
          }
        ],
        notes: '',
        discount: 0,
        taxRate: 21,
        subtotal: 649.95,
        discountAmount: 0,
        taxableAmount: 649.95,
        taxAmount: 136.49,
        total: 786.44
      }
    ];

    setQuotations(mockQuotations);
  }, []);

  // Check for mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSelectQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setCurrentView('preview');
    setIsEditing(false);
  };

  const handleCreateNew = () => {
    setSelectedQuotation(null);
    setCurrentView('form');
    setIsEditing(false);
  };

  const handleEdit = () => {
    setCurrentView('form');
    setIsEditing(true);
  };

  const handleSave = (quotationData) => {
    if (isEditing && selectedQuotation) {
      // Update existing quotation
      setQuotations(prev => 
        prev.map(q => 
          q.id === selectedQuotation.id 
            ? { ...quotationData, id: selectedQuotation.id }
            : q
        )
      );
      setSelectedQuotation({ ...quotationData, id: selectedQuotation.id });
    } else {
      // Create new quotation
      const newQuotation = {
        ...quotationData,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      setQuotations(prev => [newQuotation, ...prev]);
      setSelectedQuotation(newQuotation);
    }
    
    setCurrentView('preview');
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (selectedQuotation) {
      setCurrentView('preview');
    } else {
      setCurrentView('list');
    }
    setIsEditing(false);
  };

  const handleConvertToInvoice = () => {
    console.log('Convert to invoice:', selectedQuotation);
    // In real app, navigate to invoice creation with quotation data
    window.location.href = `/invoice-management?from=quotation&id=${selectedQuotation.id}`;
  };

  const handleDuplicate = () => {
    if (selectedQuotation) {
      const duplicatedQuotation = {
        ...selectedQuotation,
        id: Date.now(),
        number: `${selectedQuotation.number}-COPY`,
        status: 'draft',
        date: new Date().toISOString().split('T')[0],
        expiryDate: '',
        createdAt: new Date().toISOString()
      };
      
      setQuotations(prev => [duplicatedQuotation, ...prev]);
      setSelectedQuotation(duplicatedQuotation);
      setCurrentView('form');
      setIsEditing(false);
    }
  };

  const handleDelete = () => {
    if (selectedQuotation && window.confirm('¿Está seguro de que desea eliminar este presupuesto?')) {
      setQuotations(prev => prev.filter(q => q.id !== selectedQuotation.id));
      setSelectedQuotation(null);
      setCurrentView('list');
    }
  };

  const renderMobileView = () => {
    switch (currentView) {
      case 'form':
        return (
          <QuotationForm
            quotation={selectedQuotation}
            onSave={handleSave}
            onCancel={handleCancel}
            isEditing={isEditing}
          />
        );
      case 'preview':
        return (
          <QuotationPreview
            quotation={selectedQuotation}
            onEdit={handleEdit}
            onConvertToInvoice={handleConvertToInvoice}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
          />
        );
      default:
        return (
          <QuotationList
            quotations={quotations}
            selectedQuotation={selectedQuotation}
            onSelectQuotation={handleSelectQuotation}
            onCreateNew={handleCreateNew}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation />
          
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold text-text-primary mb-2">
              Gestión de Presupuestos
            </h1>
            <p className="text-text-secondary">
              Cree, gestione y convierta presupuestos en facturas
            </p>
          </div>

          <QuotationStats quotations={quotations} />

          {isMobile ? (
            <div className="h-[calc(100vh-280px)]">
              {renderMobileView()}
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-6 h-[calc(100vh-280px)]">
              {/* Left Panel - Quotation List */}
              <div className="col-span-4">
                <QuotationList
                  quotations={quotations}
                  selectedQuotation={selectedQuotation}
                  onSelectQuotation={handleSelectQuotation}
                  onCreateNew={handleCreateNew}
                />
              </div>

              {/* Right Panel - Form or Preview */}
              <div className="col-span-8">
                {currentView === 'form' ? (
                  <QuotationForm
                    quotation={selectedQuotation}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    isEditing={isEditing}
                  />
                ) : selectedQuotation ? (
                  <QuotationPreview
                    quotation={selectedQuotation}
                    onEdit={handleEdit}
                    onConvertToInvoice={handleConvertToInvoice}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                ) : (
                  <div className="bg-surface rounded-lg border border-border h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="FileText" size={32} className="text-primary" />
                      </div>
                      <h3 className="text-lg font-medium text-text-primary mb-2">
                        Seleccione un Presupuesto
                      </h3>
                      <p className="text-text-muted mb-4">
                        Elija un presupuesto de la lista para ver los detalles
                      </p>
                      <Button
                        variant="primary"
                        onClick={handleCreateNew}
                        iconName="Plus"
                      >
                        Crear Nuevo Presupuesto
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <QuickActionFAB />
    </div>
  );
};

export default QuotationManagement;